/**
 * sample-bundle.js
 *
 * Single source of truth for auto-pairing complimentary 2ML samples with
 * full-size (50 ML / 55 ML) fragrance purchases.
 *
 * Replaces three racing scripts that previously fought over the cart:
 *   - layout/theme.liquid (two PDP submit handlers, both unawaited)
 *   - assets/app.js (injectSampleProducts checkbox UI + destructive clearCart)
 *
 * Strategy: react to cart changes AFTER they happen, then add/remove samples
 * to match the desired state. Idempotent — every pass converges the cart
 * toward "every full-size line has its two paired samples; no orphans".
 */

(function () {
  'use strict';

  // ── Constants ──────────────────────────────────────────────────────────────
  var SIZE_PATTERN = /^5[05]\s*ml$/i; // matches "50 ML" / "55 ML" exactly
  var FREE_SAMPLE_PROP = '_free_sample';
  var FREE_SAMPLE_FOR_PROP = '_free_sample_for';

  // ── State ──────────────────────────────────────────────────────────────────
  var reconcileInFlight = false;
  var reconcileQueued = false;

  // ── Utils ──────────────────────────────────────────────────────────────────
  function getPairedSampleVariantIds(productId) {
    var map = window.complementarySamples || {};
    var entry = map[String(productId)];
    if (!entry || !entry.length) return [];
    return entry
      .filter(function (s) { return s && s.available && s.variant_id; })
      .map(function (s) { return Number(s.variant_id); });
  }

  function isFullSizeLine(item) {
    if (!item) return false;
    // Prefer options_with_values for robust size detection
    if (Array.isArray(item.options_with_values)) {
      var sizeOpt = item.options_with_values.find(function (opt) {
        return opt && typeof opt.name === 'string' && opt.name.toLowerCase() === 'size';
      });
      if (sizeOpt && typeof sizeOpt.value === 'string') {
        return SIZE_PATTERN.test(sizeOpt.value.trim());
      }
    }
    // Fallback to variant_title
    if (typeof item.variant_title === 'string') {
      return SIZE_PATTERN.test(item.variant_title.trim());
    }
    return false;
  }

  function isFreeSampleLine(item) {
    if (!item) return false;
    if (item.properties && item.properties[FREE_SAMPLE_PROP] === 'true') return true;
    // Fallback for sample lines added without the property (e.g. manually via
    // the /collections/samples page), matching the OR-handle check in c-cart.liquid.
    if (typeof item.handle === 'string' && item.handle.indexOf('2-ml') !== -1) return true;
    return false;
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (res) {
      if (!res.ok) {
        return res.text().then(function (text) {
          var err = new Error('Request failed: ' + res.status + ' ' + url);
          err.status = res.status;
          err.body = text;
          throw err;
        });
      }
      return res.json();
    });
  }

  function refreshCartUI() {
    // liquid-ajax-cart exposes .update() to re-sync the cart UI with /cart.js.
    // It will fire another request-end, which triggers reconcile again, but
    // reconcile is idempotent so the second pass exits fast.
    if (window.liquidAjaxCart && typeof window.liquidAjaxCart.update === 'function') {
      try { window.liquidAjaxCart.update(); } catch (e) {}
    }
    if (typeof window.reloadCoretexCart === 'function') {
      try { window.reloadCoretexCart(); } catch (e) {}
    }
  }

  // ── Core: reconcile cart state ─────────────────────────────────────────────
  function reconcileSamples() {
    if (reconcileInFlight) {
      reconcileQueued = true;
      return Promise.resolve();
    }
    reconcileInFlight = true;

    return fetchJSON('/cart.js')
      .then(function (cart) {
        var items = cart.items || [];

        // Build the set of full-size parent variants currently in cart.
        var fullSizeParents = items
          .filter(isFullSizeLine)
          .map(function (item) { return String(item.variant_id); });

        // Compute desired sample variants based on parents.
        // Use Set to dedupe across multiple full-size purchases sharing samples.
        var desiredSamples = new Set();
        items.forEach(function (item) {
          if (!isFullSizeLine(item)) return;
          getPairedSampleVariantIds(item.product_id).forEach(function (id) {
            desiredSamples.add(Number(id));
          });
        });

        // Existing free-sample lines in cart.
        var existingSampleLines = items.filter(isFreeSampleLine);
        var existingSampleVariantIds = new Set(
          existingSampleLines.map(function (item) { return Number(item.variant_id); })
        );

        // Missing samples: desired but not present.
        var missing = [];
        desiredSamples.forEach(function (id) {
          if (!existingSampleVariantIds.has(id)) missing.push(id);
        });

        // Orphan samples: present but no longer desired (parent removed).
        var orphans = existingSampleLines.filter(function (item) {
          return !desiredSamples.has(Number(item.variant_id));
        });

        var ops = [];

        if (missing.length) {
          var parentVariantForLog = fullSizeParents[0] || '';
          ops.push(
            fetchJSON('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                items: missing.map(function (id) {
                  return {
                    id: id,
                    quantity: 1,
                    properties: (function () {
                      var p = {};
                      p[FREE_SAMPLE_PROP] = 'true';
                      if (parentVariantForLog) p[FREE_SAMPLE_FOR_PROP] = parentVariantForLog;
                      return p;
                    })(),
                  };
                }),
              }),
            }).catch(function (err) {
              console.warn('[sample-bundle] add samples failed', err);
            })
          );
        }

        if (orphans.length) {
          var updates = {};
          orphans.forEach(function (line) { updates[line.key] = 0; });
          ops.push(
            fetchJSON('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates: updates }),
            }).catch(function (err) {
              console.warn('[sample-bundle] remove orphan samples failed', err);
            })
          );
        }

        if (!ops.length) return null;
        return Promise.all(ops).then(function () { return true; });
      })
      .then(function (mutated) {
        if (mutated) refreshCartUI();
      })
      .catch(function (err) {
        console.warn('[sample-bundle] reconcile failed', err);
      })
      .then(function () {
        reconcileInFlight = false;
        if (reconcileQueued) {
          reconcileQueued = false;
          // Defer the next pass so we don't recurse on the microtask queue.
          setTimeout(reconcileSamples, 0);
        }
      });
  }

  // Expose globally so c-cart.js.liquid (and any other legacy callsite) can invoke it.
  window.reconcileSamples = reconcileSamples;

  // ── Event wiring: liquid-ajax-cart lifecycle ──────────────────────────────
  document.addEventListener('liquid-ajax-cart:request-end', function (event) {
    var detail = event && event.detail;
    var requestState = detail && detail.requestState;
    if (!requestState) return;
    var type = requestState.requestType;
    if (type !== 'add' && type !== 'change' && type !== 'update') return;
    // Only reconcile on successful upstream mutations.
    if (!requestState.responseData || requestState.responseData.ok === false) return;
    reconcileSamples();
  });

  // ── Checkout guard ─────────────────────────────────────────────────────────
  // Block submission of the cart form if the cart contains only complimentary
  // samples. This is the final defense against the race between removing a
  // paid item and reconcileSamples cleaning up the orphan samples.
  function isCheckoutForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    if (form.id === 'coretex-cart-form') return true;
    if (form.getAttribute('action') === '/cart') return true;
    return false;
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!isCheckoutForm(form)) return;

      // Capture form first; we will resubmit programmatically after the check.
      event.preventDefault();
      event.stopImmediatePropagation();

      var submitBtn = form.querySelector('button[type="submit"], #checkout');
      if (submitBtn) submitBtn.setAttribute('aria-disabled', 'true');

      // form.submit() drops the activated submitter's name/value, so the
      // "checkout" field from the button is missing and Shopify redirects to
      // /cart instead of /checkout. Add a hidden input so the field is always
      // posted regardless of how we resubmit.
      function ensureCheckoutField() {
        if (form.querySelector('input[type="hidden"][name="checkout"]')) return;
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'checkout';
        hidden.value = '';
        form.appendChild(hidden);
      }

      fetchJSON('/cart.js')
        .then(function (cart) {
          var hasPaid = (cart.items || []).some(function (item) {
            return !isFreeSampleLine(item);
          });
          if (hasPaid) {
            // Cart is valid — submit for real (.submit() bypasses our listener).
            ensureCheckoutField();
            form.submit();
            return;
          }
          // Only samples in cart. Trigger reconcile to clean them up,
          // then refresh the drawer so the empty state renders.
          if (submitBtn) submitBtn.removeAttribute('aria-disabled');
          reconcileSamples();
        })
        .catch(function () {
          // On any network error, fall back to letting Shopify decide.
          ensureCheckoutField();
          form.submit();
        });
    },
    true // capture phase, so we beat any other listener
  );

  // While liquid-ajax-cart is mid-request, mark the cart as processing so
  // CSS can disable the checkout button visually for ANY mutation, not just
  // 'add' (which was the previous behavior in c-cart.js.liquid).
  document.addEventListener('liquid-ajax-cart:request-start', function (event) {
    var detail = event && event.detail;
    var requestState = detail && detail.requestState;
    if (!requestState) return;
    if (requestState.requestType === 'change' || requestState.requestType === 'update') {
      document.body.setAttribute('cart-state', 'processing');
    }
  });
  document.addEventListener('liquid-ajax-cart:request-end', function (event) {
    var detail = event && event.detail;
    var requestState = detail && detail.requestState;
    if (!requestState) return;
    if (requestState.requestType === 'change' || requestState.requestType === 'update') {
      document.body.removeAttribute('cart-state');
    }
  });

  // ── PDP: visibility of the .sample_wrapper based on selected size ─────────
  function syncSampleWrapperVisibility() {
    var wrapper = document.querySelector('.sample_wrapper');
    if (!wrapper) return;

    function update(value) {
      if (typeof value === 'string' && SIZE_PATTERN.test(value.trim())) {
        wrapper.style.display = '';
      } else {
        wrapper.style.display = 'none';
      }
    }

    var initial = document.querySelector('input[type="radio"][name^="options"]:checked')
      || document.querySelector('input[type="radio"]:checked');
    update(initial ? initial.value : null);

    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.type === 'radio') update(t.value);
    });
  }

  // ── Bootstrap on DOM ready ─────────────────────────────────────────────────
  function init() {
    syncSampleWrapperVisibility();
    // Run one reconcile on load so a reopened cart with stale samples self-heals.
    reconcileSamples();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
